/**
 * ReminderCard — firmenweiter Standard für Stempel-Erinnerungen
 *
 * • reminder_threshold_minutes: Minuten nach der Ø-Stempelzeit, ab denen erinnert wird (0–120)
 *
 * Gilt firmenweit als Standard; pro Mitarbeiter überschreibbar (Mitarbeiter-Formular).
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { BellRing, FileSignature, Coffee, AlarmClock, LogOut } from 'lucide-react';
import { Switch } from '../../components/ui/switch';

const PRESETS = [
  { v: 5, label: '5 min' },
  { v: 10, label: '10 min' },
  { v: 15, label: '15 min' },
  { v: 30, label: '30 min' },
];

const BREAK_PRESETS = [
  { v: 30, label: '30 min' },
  { v: 45, label: '45 min' },
  { v: 60, label: '60 min' },
  { v: 0, label: 'Aus' },
];

const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

export const ReminderCard = ({ formData, onChange }) => {
  const threshold = Number(formData.reminder_threshold_minutes ?? 10);

  return (
    <Card className="border-border/60" data-testid="reminder-card">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <BellRing className="h-5 w-5 text-primary" />
          Stempel-Erinnerungen
        </CardTitle>
        <CardDescription>
          Firmenweiter Standard: Wie viele Minuten nach der üblichen (durchschnittlichen)
          Stempelzeit eines Mitarbeiters erinnert werden soll. Pro Mitarbeiter überschreibbar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Label htmlFor="reminder_threshold_minutes">Erinnern nach (Minuten)</Label>
        <Input
          id="reminder_threshold_minutes"
          type="number"
          min={0}
          max={120}
          step={1}
          value={threshold}
          onChange={(e) => onChange('reminder_threshold_minutes', clamp(parseInt(e.target.value || '0', 10) || 0, 0, 120))}
          data-testid="input-reminder-threshold"
        />
        <div className="flex flex-wrap gap-2 pt-1">
          {PRESETS.map((p) => (
            <button
              key={p.v}
              type="button"
              onClick={() => onChange('reminder_threshold_minutes', p.v)}
              className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                threshold === p.v
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border hover:bg-muted'
              }`}
              data-testid={`preset-reminder-${p.v}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          0–120 Minuten. Beispiel: Stempelt jemand üblicherweise um 08:00 ein und ist um 08:10
          noch nicht eingestempelt, erscheint eine Erinnerung (bei 10 Min.).
          Urlaub und Abwesenheit werden automatisch berücksichtigt.
        </p>

        {/* Pausen-Erinnerung (firmenweiter Standard) */}
        <div className="pt-4 border-t border-border/40 space-y-3">
          <Label htmlFor="break_reminder_minutes" className="flex items-center gap-2">
            <Coffee className="h-4 w-4 text-primary" />
            Pausen-Erinnerung
          </Label>
          <p className="text-xs text-muted-foreground">
            Erinnert Mitarbeiter (In-App + Handy-Push), wenn ihre laufende Pause länger
            als der eingestellte Wert dauert. 0 = deaktiviert.
          </p>
          <Input
            id="break_reminder_minutes"
            type="number"
            min={0}
            max={240}
            step={1}
            value={Number(formData.break_reminder_minutes ?? 30)}
            onChange={(e) => onChange('break_reminder_minutes', clamp(parseInt(e.target.value || '0', 10) || 0, 0, 240))}
            className="max-w-[160px]"
            data-testid="input-break-reminder-minutes"
          />
          <div className="flex flex-wrap gap-2 pt-1">
            {BREAK_PRESETS.map((p) => (
              <button
                key={p.v}
                type="button"
                onClick={() => onChange('break_reminder_minutes', p.v)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                  Number(formData.break_reminder_minutes ?? 30) === p.v
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:bg-muted'
                }`}
                data-testid={`preset-break-reminder-${p.v}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Vor-Dienstbeginn-Erinnerung (firmenweiter Standard) */}
        <div className="pt-4 border-t border-border/40 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <AlarmClock className="h-4 w-4 text-primary" />
              Erinnerung vor Dienstbeginn
            </Label>
            <Switch
              checked={formData.pre_shift_reminder_enabled ?? true}
              onCheckedChange={(v) => onChange('pre_shift_reminder_enabled', v)}
              data-testid="switch-pre-shift-reminder"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Erinnert Mitarbeiter per Handy-Push, sich rechtzeitig einzustempeln – so viele Minuten
            vor dem geplanten Dienstbeginn (aus dem Dienstplan). Pro Mitarbeiter überschreibbar.
            Nur an Tagen mit geplanter Schicht; Urlaub/Abwesenheit/Feiertag werden berücksichtigt.
          </p>
          <Label htmlFor="pre_shift_reminder_minutes">Vorlaufzeit (Minuten vorher)</Label>
          <Input
            id="pre_shift_reminder_minutes"
            type="number"
            min={0}
            max={180}
            step={1}
            disabled={!(formData.pre_shift_reminder_enabled ?? true)}
            value={Number(formData.pre_shift_reminder_minutes ?? 15)}
            onChange={(e) => onChange('pre_shift_reminder_minutes', clamp(parseInt(e.target.value || '0', 10) || 0, 0, 180))}
            className="max-w-[160px]"
            data-testid="input-pre-shift-reminder-minutes"
          />
          <div className="flex flex-wrap gap-2 pt-1">
            {[10, 15, 30, 45].map((v) => (
              <button
                key={v}
                type="button"
                disabled={!(formData.pre_shift_reminder_enabled ?? true)}
                onClick={() => onChange('pre_shift_reminder_minutes', v)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors disabled:opacity-40 ${
                  Number(formData.pre_shift_reminder_minutes ?? 15) === v
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:bg-muted'
                }`}
                data-testid={`preset-pre-shift-${v}`}
              >
                {v} min
              </button>
            ))}
          </div>
        </div>

        {/* Vor-Feierabend-Erinnerung (firmenweiter Standard) */}
        <div className="pt-4 border-t border-border/40 space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <LogOut className="h-4 w-4 text-primary" />
              Erinnerung vor Feierabend
            </Label>
            <Switch
              checked={formData.end_shift_reminder_enabled ?? true}
              onCheckedChange={(v) => onChange('end_shift_reminder_enabled', v)}
              data-testid="switch-end-shift-reminder"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Erinnert eingestempelte Mitarbeiter per Handy-Push ans Ausstempeln – so viele Minuten
            vor dem geplanten Dienstende (aus dem Dienstplan). Pro Mitarbeiter überschreibbar.
          </p>
          <Label htmlFor="end_shift_reminder_minutes">Vorlaufzeit (Minuten vorher)</Label>
          <Input
            id="end_shift_reminder_minutes"
            type="number"
            min={0}
            max={180}
            step={1}
            disabled={!(formData.end_shift_reminder_enabled ?? true)}
            value={Number(formData.end_shift_reminder_minutes ?? 5)}
            onChange={(e) => onChange('end_shift_reminder_minutes', clamp(parseInt(e.target.value || '0', 10) || 0, 0, 180))}
            className="max-w-[160px]"
            data-testid="input-end-shift-reminder-minutes"
          />
          <div className="flex flex-wrap gap-2 pt-1">
            {[5, 10, 15, 30].map((v) => (
              <button
                key={v}
                type="button"
                disabled={!(formData.end_shift_reminder_enabled ?? true)}
                onClick={() => onChange('end_shift_reminder_minutes', v)}
                className={`text-xs rounded-full border px-2.5 py-1 transition-colors disabled:opacity-40 ${
                  Number(formData.end_shift_reminder_minutes ?? 5) === v
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border hover:bg-muted'
                }`}
                data-testid={`preset-end-shift-${v}`}
              >
                {v} min
              </button>
            ))}
          </div>
        </div>

        {/* Vertrags-Ablauf-Erinnerung (firmenweiter Standard) */}
        <div className="pt-4 border-t border-border/40 space-y-3">
          <Label className="flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-primary" />
            Vertrags-Ablauf-Erinnerung
          </Label>
          <p className="text-xs text-muted-foreground">
            Vorlaufzeit, mit der Berechtigte (Bearbeiten-Recht Mitarbeiter) vor dem Ablauf eines
            befristeten Vertrags benachrichtigt werden. Pro Vertrag überschreibbar.
          </p>
          <div className="flex gap-2">
            <Input
              type="number"
              min={1}
              max={365}
              value={Number(formData.contract_expiry_reminder_value ?? 30)}
              onChange={(e) => onChange('contract_expiry_reminder_value', clamp(parseInt(e.target.value || '0', 10) || 1, 1, 365))}
              className="max-w-[160px]"
              data-testid="input-contract-expiry-value"
            />
            <Select
              value={formData.contract_expiry_reminder_unit || 'days'}
              onValueChange={(v) => onChange('contract_expiry_reminder_unit', v)}
            >
              <SelectTrigger className="w-[150px]" data-testid="select-contract-expiry-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="days">Tage vorher</SelectItem>
                <SelectItem value="months">Monate vorher</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReminderCard;
